import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';
import SelectRecherche from '../components/SelectRecherche';
import '../assets/styles/Historique.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';

const LIMIT = 30;

/** Les statuts du journal, en clair — le reste s'affiche tel quel. */
/** « 14:32 » — la journée est déjà dite par l'intertitre. */
function heureCourte(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** « jeudi 2 octobre 2026 » — l'intertitre d'une journée du fil. */
function jourLong(iso) {
  const texte = new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/**
 * La nature d'une action, lue dans son commentaire : elle donne la couleur
 * de la ligne. Le journal n'enregistre pas de type — le verbe suffit.
 */
function natureAction(l) {
  const texte = String(l.commentaire || '');
  if (/supprim/i.test(texte)) return { cle: 'suppression', label: 'Suppression' };
  if (/relance/i.test(texte)) return { cle: 'relance', label: 'Relance' };
  if (/sign[ée]/i.test(texte)) return { cle: 'signature', label: 'Signature' };
  if (/envoy|email|convocation|rappel|lien d/i.test(texte)) return { cle: 'envoi', label: 'Envoi' };
  if (/génér|créé|établi|posé|import|déposé|validé/i.test(texte)) return { cle: 'creation', label: 'Création' };
  return { cle: 'statut', label: 'Statut' };
}

/** Une teinte stable par utilisateur : le fil se balaie par couleur. */
function teinteUtilisateur(nom) {
  const texte = String(nom || 'auto');
  let somme = 0;
  for (let i = 0; i < texte.length; i += 1) somme = (somme + texte.charCodeAt(i) * (i + 1)) % 9973;
  const h = [8, 32, 96, 150, 190, 215, 260, 290, 330][somme % 9];
  return { background: `hsl(${h} 45% 92%)`, color: `hsl(${h} 55% 28%)` };
}

const STATUTS = {
  a_traiter: 'À traiter',
  nouveau: 'Nouveau',
  devis_envoye: 'Devis envoyé',
  devis_a_envoyer: 'Devis à envoyer',
  devis_accepte: 'Devis accepté',
  convocation_envoye: 'Convocation envoyée',
  contract_envoye: 'Contrat',
  sans_suite: 'Sans suite',
};

/**
 * Menu Historique : le journal des actions, toutes demandes confondues —
 * qui a fait quoi, et quand. Filtrable par utilisateur et par période, avec
 * une recherche libre (client, commentaire, numéro de document…).
 */
function Historique() {
  const navigate = useNavigate();
  const [lignes, setLignes] = useState([]);
  const [total, setTotal] = useState(0);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [utilisateur, setUtilisateur] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setRecherche(saisie);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [saisie]);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (recherche) query.set('search', recherche);
    if (utilisateur) query.set('utilisateur', utilisateur);
    if (debut) query.set('debut', debut);
    if (fin) query.set('fin', fin);
    query.set('page', String(page));
    query.set('limit', String(LIMIT));
    return apiFetch(`/historique?${query.toString()}`)
      .then((res) => {
        setLignes(res.data || []);
        setTotal(res.total || 0);
        setUtilisateurs(res.utilisateurs || []);
      })
      .catch((err) => setError(err.message || 'Chargement impossible.'))
      .finally(() => setLoading(false));
  }, [recherche, utilisateur, debut, fin, page]);

  useEffect(() => {
    charger();
  }, [charger]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const filtreActif = Boolean(recherche || utilisateur || debut || fin);

  // Le fil se lit par journée : un intertitre par date, les heures dessous.
  const groupes = [];
  for (const l of lignes) {
    const jour = String(l.date_maj).slice(0, 10);
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.jour === jour) dernier.lignes.push(l);
    else groupes.push({ jour, libelle: jourLong(l.date_maj), lignes: [l] });
  }

  return (
    <div className="devis-page devis-page-scroll">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Historique</h1>
          <p className="devis-subtitle">
            Le journal des actions : qui a fait quoi, sur quel dossier, et quand.
          </p>
        </div>
      </div>

      <div className="devis-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
        <input
          type="search"
          className="devis-search"
          placeholder="Rechercher (client, commentaire, numéro, email...)"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        <label className="devis-form-field" style={{ minWidth: 180 }}>
          <span>Utilisateur</span>
          <SelectRecherche
            value={utilisateur}
            onChange={(e) => {
              setUtilisateur(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous</option>
            <option value="systeme">Automatique (planificateur)</option>
            {utilisateurs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </SelectRecherche>
        </label>
        <label className="devis-form-field">
          <span>Du</span>
          <input
            type="date"
            value={debut}
            onChange={(e) => {
              setDebut(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="devis-form-field">
          <span>Au</span>
          <input
            type="date"
            value={fin}
            min={debut || undefined}
            onChange={(e) => {
              setFin(e.target.value);
              setPage(1);
            }}
          />
        </label>
        {filtreActif && (
          <button
            type="button"
            className="devis-btn devis-btn-sm"
            onClick={() => {
              setSaisie('');
              setUtilisateur('');
              setDebut('');
              setFin('');
              setPage(1);
            }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {loading && <div className="devis-state">Chargement de l’historique...</div>}
      {!loading && error && <div className="devis-state devis-state-error">{error}</div>}
      {!loading && !error && lignes.length === 0 && (
        <div className="devis-card clients-vide">
          {filtreActif ? 'Aucune action ne correspond à ces filtres.' : 'Aucune action enregistrée.'}
        </div>
      )}

      {!loading &&
        !error &&
        groupes.map((g) => (
          <div className="devis-card clients-demande" key={g.jour}>
            <div className="hist-jour-titre">
              {g.libelle}
              <span>{g.lignes.length}</span>
            </div>
            {g.lignes.map((l) => {
              const nature = natureAction(l);
              return (
                <div className={`hist-ligne hist-ligne-${nature.cle}`} key={l.id}>
                  <div className="hist-heure">{heureCourte(l.date_maj)}</div>
                  <div className="hist-corps">
                    <div className="hist-tete">
                      <span className={`hist-pill hist-pill-${nature.cle}`}>{nature.label}</span>
                      <span className="hist-user" style={teinteUtilisateur(l.traite_par)}>
                        {l.traite_par || 'Automatique'}
                      </span>
                      <span className="hist-societe">
                        {l.societe_nom || [l.prenom, l.nom].filter(Boolean).join(' ') || '—'}
                      </span>
                    </div>
                    <div className="hist-commentaire">
                      {l.commentaire || STATUTS[l.statut] || l.statut}
                    </div>
                  </div>
                  <div className="clients-ligne-actions">
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm"
                      onClick={() => navigate(`/demande-de-devis?contact=${l.contact_id}`)}
                    >
                      Dossier
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

      {!loading && !error && lignes.length > 0 && (
        <div className="devis-card clients-demande">
          <div className="clients-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <span className="devis-status-detail" style={{ marginRight: 'auto' }}>
              {total} action{total > 1 ? 's' : ''} · page {page}/{totalPages}
            </span>
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Précédent
            </button>
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Historique;
